import { useState } from 'react';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { MdBugReport, MdClose, MdContentCopy, MdEdit } from 'react-icons/md';

// Icône debug discrète (onglets Leads no-match et En cours) : données brutes du lead avec leur
// format, et charge utile HelloArtisan — réellement envoyée si le lead est parti, sinon celle qui
// serait envoyée aujourd'hui. Aucun appel à l'API Batiweb (GET /admin/batiweb/leads/:id/debug).
// « Modifier » corrige les réponses du lead côté leadrs (PUT /admin/batiweb/leads/:id/answers) ;
// rien n'est renvoyé à Batiweb.

interface DebugAnswer {
    field: string | null;
    variable: string | null;
    answer: any;
    type: string;
}

interface LeadDebug {
    lead: Record<string, any>;
    answers: DebugAnswer[];
    rawBody: any;
    context: { industry: string | null; subIndustry: string | null; worktype: { worktypeId: number; worktypeLabel: string | null } | null };
    preview: { ok: true; request: Record<string, any> } | { ok: false; missing: string[] };
    sentRequest: Record<string, any> | null;
    envelope: { security: Record<string, any> };
    helloArtisan: Record<string, any> | null;
}

const typeOf = (value: any) => (value === null ? 'null' : Array.isArray(value) ? 'array' : typeof value);

const TYPE_COLORS: Record<string, string> = {
    string: 'text-green-600',
    number: 'text-blue-600',
    boolean: 'text-purple-600',
    null: 'text-gray-400',
    object: 'text-orange-600',
    array: 'text-orange-600',
};

function Section({ title, children, copy }: { title: string; children: React.ReactNode; copy?: any }) {
    return (
        <div className="mb-5">
            <div className="mb-2 flex items-center gap-2">
                <div className="text-xs font-bold uppercase tracking-wide text-gray-500">{title}</div>
                {copy !== undefined && (
                    <button
                        onClick={() => navigator.clipboard?.writeText(JSON.stringify(copy, null, 2))}
                        title="Copier le JSON"
                        className="text-gray-300 hover:text-gray-600"
                    >
                        <MdContentCopy size={14} />
                    </button>
                )}
            </div>
            {children}
        </div>
    );
}

function JsonBlock({ value }: { value: any }) {
    return (
        <pre className="max-h-72 overflow-auto rounded-lg bg-gray-50 p-3 font-mono text-xs text-navy-700 dark:bg-navy-900 dark:text-gray-200">
            {JSON.stringify(value, null, 2)}
        </pre>
    );
}

// Champs de la charge utile avec leur type : c'est le format attendu par l'API (entiers pour les codes /types).
function RequestTable({ request }: { request: Record<string, any> }) {
    return (
        <table className="mb-2 w-full text-left font-mono text-xs">
            <tbody>
                {Object.entries(request).map(([key, value]) => {
                    const type = typeOf(value);
                    return (
                        <tr key={key} className="border-b border-gray-100 dark:border-navy-700">
                            <td className="py-1 pr-3 text-gray-600 dark:text-gray-300">{key}</td>
                            <td className="py-1 pr-3 text-navy-700 dark:text-white">{value === null ? '—' : String(value)}</td>
                            <td className={`py-1 ${TYPE_COLORS[type] || ''}`}>{type}</td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}

// Seules les valeurs simples sont éditables ; les réponses objet/tableau restent en lecture seule.
const isEditable = (a: DebugAnswer) => a.answer === null || ['string', 'number', 'boolean'].includes(typeof a.answer);

export default function LeadDebugButton({ leadId, onEdited }: { leadId: string; onEdited?: () => void }) {
    const [open, setOpen] = useState(false);
    const [data, setData] = useState<LeadDebug | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [editing, setEditing] = useState(false);
    const [drafts, setDrafts] = useState<Record<number, string>>({});
    const [newField, setNewField] = useState({ field: '', answer: '' });
    const [saving, setSaving] = useState(false);
    const [notice, setNotice] = useState('');

    const startEditing = () => {
        if (!data) return;
        setDrafts(Object.fromEntries(data.answers.map((a, i) => [i, isEditable(a) ? String(a.answer ?? '') : ''])));
        setNewField({ field: '', answer: '' });
        setNotice('');
        setEditing(true);
    };

    const handleSave = async () => {
        if (!data) return;
        const answers: { index?: number; field?: string; answer: string }[] = data.answers
            .map((a, i) => ({ a, i }))
            .filter(({ a, i }) => isEditable(a) && drafts[i] !== String(a.answer ?? ''))
            .map(({ i }) => ({ index: i, answer: drafts[i] }));
        if (newField.field.trim() && newField.answer.trim()) answers.push({ field: newField.field, answer: newField.answer });
        if (!answers.length) {
            setEditing(false);
            return;
        }
        try {
            setSaving(true);
            setError('');
            const res = await axios.put(`${CONFIG.apiUrl}/admin/batiweb/leads/${leadId}/answers`, { answers }, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            setData(res.data.debug);
            setNotice(res.data.changes.length ? `Modifié : ${res.data.changes.join(' · ')}` : 'Aucune modification.');
            setEditing(false);
            onEdited?.();
        } catch (e: any) {
            setError(e.response?.data?.message || "Impossible d'enregistrer les modifications.");
        } finally {
            setSaving(false);
        }
    };

    const handleOpen = async () => {
        setOpen(true);
        setError('');
        setEditing(false);
        setNotice('');
        try {
            setLoading(true);
            const res = await axios.get(`${CONFIG.apiUrl}/admin/batiweb/leads/${leadId}/debug`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            setData(res.data);
        } catch (e: any) {
            setError(e.response?.data?.message || 'Impossible de charger le debug du lead.');
        } finally {
            setLoading(false);
        }
    };

    const request = data?.sentRequest ?? (data?.preview.ok ? data.preview.request : null);

    return (
        <>
            <button onClick={handleOpen} title="Debug : données du lead et charge utile Batiweb" className="text-gray-300 hover:text-gray-500 dark:text-navy-500 dark:hover:text-gray-300">
                <MdBugReport size={15} />
            </button>

            {open && (
                <div className="fixed inset-0 z-[1000] flex items-center justify-center">
                    <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setOpen(false)} />
                    <div className="relative z-10 m-4 flex max-h-[90vh] w-full max-w-4xl flex-col rounded-lg bg-white shadow-xl dark:bg-navy-800">
                        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-navy-600">
                            <h2 className="flex items-center gap-2 text-lg font-bold text-navy-700 dark:text-white">
                                <MdBugReport className="text-gray-400" />
                                Debug lead <span className="font-mono text-sm font-normal text-gray-400">{leadId}</span>
                            </h2>
                            <button onClick={() => setOpen(false)} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                                <MdClose size={22} />
                            </button>
                        </div>

                        <div className="overflow-y-auto px-6 py-4">
                            {loading && <div className="py-10 text-center text-gray-400">Chargement...</div>}
                            {error && <div className="mb-3 rounded-lg bg-red-100 p-3 text-sm text-red-700">{error}</div>}
                            {notice && <div className="mb-3 rounded-lg bg-green-50 p-3 text-xs text-green-700">{notice}</div>}

                            {data && !loading && (
                                <div className="grid grid-cols-1 gap-x-6 lg:grid-cols-2">
                                    <div>
                                        <Section title="Lead" copy={data.lead}>
                                            <JsonBlock value={data.lead} />
                                        </Section>

                                        <Section title={`Réponses du formulaire (${data.answers.length})`} copy={data.rawBody}>
                                            <div className="mb-2 flex justify-end gap-2">
                                                {editing ? (
                                                    <>
                                                        <button onClick={() => setEditing(false)} disabled={saving} className="rounded-lg px-3 py-1 text-xs font-semibold text-gray-500 hover:bg-gray-100 dark:hover:bg-navy-700">
                                                            Annuler
                                                        </button>
                                                        <button onClick={handleSave} disabled={saving} className="rounded-lg bg-brand-500 px-3 py-1 text-xs font-semibold text-white hover:bg-brand-600 disabled:opacity-50">
                                                            {saving ? 'Enregistrement...' : 'Enregistrer'}
                                                        </button>
                                                    </>
                                                ) : (
                                                    <button onClick={startEditing} className="flex items-center gap-1 rounded-lg bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-200">
                                                        <MdEdit /> Modifier
                                                    </button>
                                                )}
                                            </div>
                                            <table className="mb-2 w-full text-left font-mono text-xs">
                                                <thead>
                                                    <tr className="text-gray-400">
                                                        <th className="pb-1 pr-3 font-normal">field</th>
                                                        <th className="pb-1 pr-3 font-normal">variable</th>
                                                        <th className="pb-1 pr-3 font-normal">answer</th>
                                                        <th className="pb-1 font-normal">type</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {data.answers.map((a, i) => (
                                                        <tr key={i} className="border-b border-gray-100 align-top dark:border-navy-700">
                                                            <td className="py-1 pr-3 text-gray-600 dark:text-gray-300">{a.field ?? '—'}</td>
                                                            <td className="py-1 pr-3 text-gray-400">{a.variable ?? '—'}</td>
                                                            <td className="py-1 pr-3 text-navy-700 dark:text-white">
                                                                {editing && isEditable(a) ? (
                                                                    <input
                                                                        value={drafts[i] ?? ''}
                                                                        onChange={(e) => setDrafts((d) => ({ ...d, [i]: e.target.value }))}
                                                                        className="w-full rounded border border-gray-300 px-1.5 py-0.5 font-mono text-xs dark:border-navy-600 dark:bg-navy-900"
                                                                    />
                                                                ) : typeof a.answer === 'object' && a.answer !== null ? (
                                                                    JSON.stringify(a.answer)
                                                                ) : (
                                                                    String(a.answer ?? '—')
                                                                )}
                                                            </td>
                                                            <td className={`py-1 ${TYPE_COLORS[a.type] || ''}`}>{a.type}</td>
                                                        </tr>
                                                    ))}
                                                    {editing && (
                                                        <tr className="align-top">
                                                            <td className="py-1 pr-3" colSpan={2}>
                                                                <input
                                                                    placeholder="Nouveau champ (ex. Email)"
                                                                    value={newField.field}
                                                                    onChange={(e) => setNewField((f) => ({ ...f, field: e.target.value }))}
                                                                    className="w-full rounded border border-dashed border-gray-300 px-1.5 py-0.5 font-mono text-xs dark:border-navy-600 dark:bg-navy-900"
                                                                />
                                                            </td>
                                                            <td className="py-1 pr-3" colSpan={2}>
                                                                <input
                                                                    placeholder="Valeur"
                                                                    value={newField.answer}
                                                                    onChange={(e) => setNewField((f) => ({ ...f, answer: e.target.value }))}
                                                                    className="w-full rounded border border-dashed border-gray-300 px-1.5 py-0.5 font-mono text-xs dark:border-navy-600 dark:bg-navy-900"
                                                                />
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </Section>

                                        <Section title="Contexte résolu">
                                            <div className="font-mono text-xs text-navy-700 dark:text-gray-200">
                                                <div>industrie : {data.context.industry ?? <span className="text-red-500">inconnue</span>}</div>
                                                <div>produit : {data.context.subIndustry ?? <span className="text-red-500">inconnu</span>}</div>
                                                <div>
                                                    worktype :{' '}
                                                    {data.context.worktype ? `${data.context.worktype.worktypeId} (${data.context.worktype.worktypeLabel})` : <span className="text-red-500">non mappé</span>}
                                                </div>
                                            </div>
                                        </Section>
                                    </div>

                                    <div>
                                        <Section
                                            title={data.sentRequest ? 'Charge utile envoyée à Batiweb' : "Charge utile qui serait envoyée aujourd'hui"}
                                            copy={request ? { ...data.envelope, request } : undefined}
                                        >
                                            {request ? (
                                                <>
                                                    <RequestTable request={request} />
                                                    <JsonBlock value={{ ...data.envelope, request }} />
                                                </>
                                            ) : (
                                                <div className="rounded-lg bg-orange-50 p-3 text-xs text-orange-700">
                                                    Non envoyable — champs manquants ou invalides :{' '}
                                                    {data.preview.ok === false && data.preview.missing.join(', ')}
                                                </div>
                                            )}
                                        </Section>

                                        {data.sentRequest && data.preview.ok && JSON.stringify(data.sentRequest) !== JSON.stringify(data.preview.request) && (
                                            <Section title="Différence : ce qui serait envoyé aujourd'hui">
                                                <div className="mb-2 text-xs text-orange-600">Le matching ou les réponses ont changé depuis l'envoi.</div>
                                                <RequestTable request={data.preview.request} />
                                            </Section>
                                        )}

                                        {data.helloArtisan && (
                                            <Section title="État Batiweb (lead.helloArtisan)" copy={data.helloArtisan}>
                                                <JsonBlock value={Object.fromEntries(Object.entries(data.helloArtisan).filter(([k]) => k !== 'sentRequest'))} />
                                            </Section>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
